import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Organisation analytics - totals only. Mirrors getOrganizationAnalytics in
 * functions/src/api/analytics.ts: every value is a count, and show titles are
 * the only text. Nothing here identifies a person.
 */

export type AnalyticsRange = 7 | 30 | 90;
export type ShowHealth = 'thriving' | 'quiet' | 'inactive' | 'ended' | 'counting';

export interface AnalyticsWeek {
  /** Monday of the week, YYYY-MM-DD (UTC). */
  weekStart: string;
  activePeople: number;
  mobile: number;
  web: number;
}

export interface AnalyticsShow {
  id: string;
  title: string;
  cast: number;
  /** This week so far - still filling. */
  activeThisWeek: number;
  /** Last complete week. */
  activeLastWeek: number;
  resources: number;
  contentAdded: number;
  /** YYYY-MM-DD, or null when nothing is booked. */
  nextRehearsal: string | null;
  rehearsalSoon: boolean;
  ended: boolean;
  health: ShowHealth;
}

export interface OrganizationAnalytics {
  generatedAt: string;
  rangeDays: AnalyticsRange;
  people: {
    total: number;
    admins: number;
    teachers: number;
    students: number;
    accepted: number;
    pending: number;
    disabled: number;
    pendingOverAWeek: number;
  };
  activity: {
    /** This week so far - still filling, so never the headline. */
    thisWeek: number;
    /** The last complete week - the headline figure. */
    lastWeek: number;
    /** The week before that, for the comparison. */
    weekBefore: number;
    /** Average of the complete weeks since counting began; null before there is one. */
    averageWeekly: number | null;
    /** Monday of the first week anyone was counted, or null. */
    countingSince: string | null;
    /** Share of the twelve weeks' platform counts that were the app; null with no data. */
    mobileShare: number | null;
    weeks: AnalyticsWeek[];
  };
  content: { added: number; previous: number; resources: number; posts: number; photos: number };
  rehearsals: { upcoming: number; showsWithUpcoming: number };
  library: { total: number; bytes: number; groups: Array<{ key: string; label: string; count: number }> };
  newsfeed: { posts: number; comments: number; reactions: number; reactionsPerPost: number; photos: number };
  shows: AnalyticsShow[];
  attention: { pendingOverAWeek: number; showsWithoutRehearsals: number; inactiveShows: number };
}

export const getOrganizationAnalytics = async (
  organizationId: string,
  rangeDays: AnalyticsRange,
): Promise<OrganizationAnalytics> => {
  const callable = httpsCallable(functions, 'getOrganizationAnalytics', { timeout: 120_000 });
  const result = await callable({ organizationId, rangeDays });
  return result.data as OrganizationAnalytics;
};

/** Accounts already counted in this page load - the server dedupes too. */
const recordedThisSession = new Set<string>();

/**
 * Count this person towards their organisation's weekly sign-in totals. Once
 * per page load; the server makes every call after the first each week a
 * no-op and keeps no history of the person. Best-effort: a failure here must
 * never touch the session.
 */
export const recordWebActivity = (uid: string): void => {
  if (recordedThisSession.has(uid)) return;
  recordedThisSession.add(uid);
  httpsCallable(functions, 'recordActivity')({ platform: 'web' }).catch(() => {
    // Analytics only - nothing to tell the user.
  });
};

/** Test seam: forget who has been counted. */
export const resetRecordedActivityForTests = (): void => {
  recordedThisSession.clear();
};
